import * as React from "react";
import { useState } from "react";
import { useTheme } from "@mui/material/styles";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  OutlinedInput,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  Button,
  TextField,
  Card,
  IconButton,
  CardHeader,
  CardContent,
  FormHelperText,
  Box,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { PhotoCamera, Delete } from "@mui/icons-material";
import {
  Prefixes,
  Genders,
  BloodGroups,
  NationalIdTypes,
  BloodGroupOptions,
} from "../Utility/Constants";
import { addPatient } from "../API/PatientsApi";
import { useSnackbar } from "../Utility/UserMessageContext";
import { useContext } from "react";
import authContext from "../Context/AuthProvider";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import { calculateAge, showLoadingImage } from "../Utility/Utility";

const maritalStatusOptions = ["Single", "Married", "Divorced", "Widowed"];

function NewPatient({ tempNewPatientId, onNewPatientAdded }) {
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();
  const { auth } = useContext(authContext);
  const [patientDetails, setPatientDetails] = useState({ photo: "" });
  const [ageInYears, setAgeInYears] = useState();
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  // Single state variable for loading state
  const [isSaving, setIsSaving] = useState(false);

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  function getStyles(option, selectedOptions, theme) {
    return {
      fontWeight: selectedOptions.includes(option)
        ? theme.typography.fontWeightMedium
        : theme.typography.fontWeightRegular,
    };
  }
  const handleChange = (e) => {
    const { name, value } = e.target;
    setPatientDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDoBChange = (value) => {
      //const { name, value } = value;
      setPatientDetails((prev) => ({
        ...prev,
        ["dateOfBirth"]: value,
      }));
      setAgeInYears(calculateAge(value));
    };

  // const handlePhotoUpload = (e) => {
  //   const file = e.target.files[0];
  //   if (file) {
  //     setPatientDetails((prev) => ({
  //       ...prev,
  //       photo: URL.createObjectURL(file),
  //     }));
  //   }
  // };
  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const fileSizeLimit = 1 * 1024 * 1024; // 1MB
    const allowedTypes = ["image/jpeg", "image/png", "image/gif"];

    if (!allowedTypes.includes(file.type)) {
      alert("Only JPG, PNG, and GIF files are allowed.");
      return;
    }

    if (file.size > fileSizeLimit) {
      alert("File size must be less than 1MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setPatientDetails((prev) => ({
        ...prev,
        photo: e.target.result,
      }));
    };
    reader.readAsDataURL(file);
  };

  const handlePhotoDelete = () => {
    setPatientDetails((prev) => ({
      ...prev,
      photo: "",
    }));
  };

  const handleSave = async () => {
    // Define required fields and their corresponding error messages
    const requiredFields = [
      {
        field: "firstName",
        message: "Filling in all mandatory fields is required",
      },
      {
        field: "prefix",
        message: "Filling in all mandatory fields is requiredx is required",
      },
      { field: "dateOfBirth", message: "Filling in all mandatory fields is required" },
      {
        field: "phone",
        message: "Filling in all mandatory fields is required",
      },
      {
        field: "gender",
        message: "Filling in all mandatory fields is required",
      },
      {
        field: "lastName",
        message: "Filling in all mandatory fields is required",
      },
    ];
    
    setIsSubmitted(true); // Set form as submitted

    // Check for missing required fields
    for (const { field, message } of requiredFields) {
      if (!patientDetails[field]) {
        showSnackbar(message, "error");
        return; // Stop execution if any required field is missing
      }
    }

    try {
      // Set saving state to true to show loading overlay
      setIsSaving(true);
      
      // If all required fields are filled, proceed to save
      const result = await addPatient(auth, patientDetails);
      if (result.isSuccess) {
        showSnackbar("Patient saved successfully", "success");
        onNewPatientAdded(tempNewPatientId);
      } else {
        showSnackbar("Failed to save patient: " + result.data, "error");
      }
    } catch (error) {
      showSnackbar("An error occurred while saving patient", "error");
    } finally {
      // Always reset saving state when operation completes
      setIsSaving(false);
    }
  };
  
  return (
    <Box sx={{ position: 'relative' }}>
      {/* Loading Overlay - using only isSaving */}
        {isSaving && (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(216, 214, 214, 0.4)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1301, // One level above Dialog
      }}
    >
      {showLoadingImage()}
    </div>
  )}
    <Grid2
      size={12}
      container
      direction="column"
      sx={{ padding: 2 }}
      spacing={1}
    >
      <Grid2 size={4}>
        <Button variant="contained" color="secondary" onClick={handleSave}>
          Save
        </Button>
      </Grid2>
      <Grid2 size={12} container spacing={1} sx={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto"}}>
        <Grid2 size={{xs:12, sm:12, md:12, lg:6, xl:6}} container spacing={2}>
          <Grid2>
            <Card variant="outlined" sx={{ padding: "10px", width: "100%" }}>
              <CardHeader
                subheader="Patient's Details"
                sx={{ paddingX: 0 }}
              ></CardHeader>
              <Grid2 container spacing={2}>
                {/* Prefix, First Name, Last Name */}
                <Grid2 size={12} container alignItems="center" spacing={2}>
                  <Grid2 size={{xs:12, sm:12, md:6, lg:6, xl:6}}>
                    <FormControl
                      fullWidth
                      margin="dense"
                      size="small"
                      required
                      error={isSubmitted && !patientDetails.prefix} // Show error only after submission
                      sx={{
                        "& .MuiFormLabel-asterisk": {
                          color: "red", // Make the asterisk red
                        },
                      }}
                    >
                      <InputLabel id="prefix-label">Prefix</InputLabel>
                      <Select
                        labelId="prefix-label"
                        name="prefix"
                        value={patientDetails.prefix || ""}
                        onChange={handleChange}
                        input={<OutlinedInput label="Prefix" />}
                        MenuProps={MenuProps}
                      >
                        {Prefixes.map((option) => (
                          <MenuItem
                            key={option}
                            value={option}
                            style={getStyles(
                              option,
                              [patientDetails.prefix],
                              theme
                            )}
                          >
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid2>
                  <Grid2 size={{xs:12, sm:12, md:6, lg:6, xl:6}}>
                    <TextField
                      margin="dense"
                      size="small"
                      variant="outlined"
                      label="Suffix"
                      name="suffix"
                      fullWidth
                      value={patientDetails.suffix || ""}
                      onChange={handleChange}
                    />
                  </Grid2>

                  <Grid2 size={{xs:12, sm:12, md:6, lg:6, xl:6}}>
                    <TextField
                      margin="dense"
                      size="small"
                      variant="outlined"
                      label="First Name"
                      name="firstName"
                      fullWidth
                      value={patientDetails.firstName || ""}
                      onChange={handleChange}
                      required // Show asterisk to indicate required field
                      error={isSubmitted && !patientDetails.firstName} // Show error only after submission
                      sx={{
                        "& .MuiFormLabel-asterisk": {
                          color: "red", // Make the asterisk red
                        },
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{xs:12, sm:12, md:6, lg:6, xl:6}}>
                    <TextField
                      margin="dense"
                      size="small"
                      variant="outlined"
                      label="Last Name"
                      name="lastName"
                      fullWidth
                      value={patientDetails.lastName || ""}
                      onChange={handleChange}
                      required
                      error={isSubmitted && !patientDetails.lastName}
                      sx={{
                        "& .MuiFormLabel-asterisk": {
                          color: "red",
                        },
                      }}
                    />
                  </Grid2>
                </Grid2>

                <Grid2 container size={12} spacing={2} direction="column">
                  {/* Row for Grid 1 and Grid 2 */}
                  <Grid2 container size={12} spacing={2}>
                    {/* Grid 1 - Age */}
                    

                    {/* Grid 2 - Date of Birth */}
                    <Grid2 size={{xs:12, sm:12, md:6, lg:6, xl:6}}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          
                          label="Date of Birth"
                          maxDate={dayjs()}
                          name="dateOfBirth"
                          fullWidth
                          required
                          value={dayjs(patientDetails.dateOfBirth)}
                          slotProps={{ textField: { size: 'small', margin: "dense"} }}
                        onChange={handleDoBChange}
                        error={isSubmitted && !patientDetails?.dateOfBirth} // Show error only after submission
                        sx={{ width: "100%",
                          "& .MuiFormLabel-asterisk": {
                            color: "red", // Make the asterisk red
                          },
                        }}
                          
                        />
                      </LocalizationProvider>
                      
                    </Grid2>
                    <Grid2 size={{xs:12, sm:12, md:6, lg:6, xl:6}}>
                      <TextField
                        margin="dense"
                        size="small"
                        label="Age"
                        name="age"
                        inputProps={{ readOnly: true }}
                        fullWidth
                        value={ageInYears || ""}
                        slotProps={{
                          inputLabel: { shrink: ageInYears },
                        }}
                      />
                    </Grid2>
                  </Grid2>

                  {/* Row for Grid 3 and Grid 4 */}
                  <Grid2 container size={12}>
                    {/* Grid 4 - Phone */}
                    <Grid2 size={{xs:12, sm:12, md:6, lg:6, xl:6}}>
                      <TextField
                        margin="dense"
                        size="small"
                        label="Phone"
                        name="phone"
                        fullWidth
                        value={patientDetails.phone || ""}
                        onChange={handleChange}
                        required // Make the field required
                        error={isSubmitted && !patientDetails.phone} // Show error if the field is empty
                        //helperText={!patientDetails.phone ? "Phone is required" : ""} // Error message
                        sx={{
                          "& .MuiFormLabel-asterisk": {
                            color: "red", // Make the asterisk red
                          },
                        }}
                      />
                    </Grid2>
                    {/* Grid 3 - Email */}
                    <Grid2 size={{xs:12, sm:12, md:6, lg:6, xl:6}}>
                      <TextField
                        margin="dense"
                        size="small"
                        label="Email"
                        name="email"
                        fullWidth
                        value={patientDetails.email}
                        onChange={handleChange}
                      />
                    </Grid2>

                    
                  </Grid2>
                </Grid2>

                {/* Other Details */}
                <Grid2 size={12} container alignItems="center" spacing={2}>
                <Grid2 size={{xs:12, sm:12, md:6, lg:4, xl:4}}>
                    <FormControl
                      fullWidth
                      margin="dense"
                      size="small"
                      required
                      error={isSubmitted && !patientDetails.gender}
                      sx={{
                        "& .MuiFormLabel-asterisk": {
                          color: "red",
                        },
                      }}
                    >
                      <InputLabel id="gender-label">Gender</InputLabel>
                      <Select
                        labelId="gender-label"
                        name="gender"
                        value={patientDetails.gender || ""}
                        onChange={handleChange}
                        input={<OutlinedInput label="Gender" />}
                        MenuProps={MenuProps} // Use MenuProps if defined
                      >
                        {Genders.map((option) => (
                          <MenuItem
                            key={option}
                            value={option}
                            style={getStyles(
                              option,
                              [patientDetails.gender],
                              theme
                            )}
                          >
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid2>
                  
                  <Grid2 size={{xs:12, sm:12, md:6, lg:4, xl:4}}>
                    <FormControl fullWidth margin="dense" size="small">
                      <InputLabel id="marital-status-label">
                        Marital Status
                      </InputLabel>
                      <Select
                        labelId="marital-status-label"
                        name="maritalStatus"
                        value={patientDetails.maritalStatus}
                        onChange={handleChange}
                        input={<OutlinedInput label="Marital Status" />}
                        MenuProps={MenuProps}
                      >
                        {maritalStatusOptions.map((option) => (
                          <MenuItem
                            key={option}
                            value={option}
                            style={getStyles(
                              option,
                              [patientDetails.maritalStatus],
                              theme
                            )}
                          >
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid2>

                  <Grid2 size={{xs:12, sm:12, md:6, lg:4, xl:4}}>
                    <FormControl fullWidth margin="dense" size="small">
                      <InputLabel id="blood-group-label">
                        Blood Group
                      </InputLabel>
                      <Select
                        labelId="blood-group-label"
                        name="bloodGroup"
                        value={patientDetails.bloodGroup}
                        onChange={handleChange}
                        input={<OutlinedInput label="Blood Group" />}
                        MenuProps={MenuProps}
                      >
                        {BloodGroupOptions.map((option) => (
                          <MenuItem
                            key={option.value}
                            value={option.value}
                            style={getStyles(
                              option,
                              [patientDetails.bloodGroup],
                              theme
                            )}
                          >
                            {option.displayValue}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid2>

                  
                </Grid2>
              </Grid2>
            </Card>
          </Grid2>

          <Grid2>
            <Card variant="outlined" sx={{ padding: "10px", width: "100%" }}>
              <CardHeader
                subheader="Address Details"
                sx={{ paddingX: 0 }}
              ></CardHeader>
              <Grid2 container spacing={2}>
                <Grid2 size={{xs:12, sm:12, md:6, lg:6, xl:6}}>
                  <TextField
                    margin="dense"
                    size="small"
                    label="House No"
                    name="addressHouseNo"
                    fullWidth
                    value={patientDetails.addressHouseNo}
                    onChange={handleChange}
                  />
                </Grid2>
                <Grid2 size={{xs:12, sm:12, md:6, lg:6, xl:6}}>
                  <TextField
                    margin="dense"
                    size="small"
                    label="Street"
                    name="addressStreet"
                    fullWidth
                    value={patientDetails.addressStreet}
                    onChange={handleChange}
                  />
                </Grid2>
                <Grid2 size={{xs:12, sm:12, md:6, lg:6, xl:6}}>
                  <TextField
                    margin="dense"
                    size="small"
                    label="City"
                    name="addressCity"
                    fullWidth
                    value={patientDetails.addressCity}
                    onChange={handleChange}
                  />
                </Grid2>
                <Grid2 size={{xs:12, sm:12, md:6, lg:6, xl:6}}>
                  <TextField
                    margin="dense"
                    size="small"
                    label="State"
                    name="addressState"
                    fullWidth
                    value={patientDetails.addressState}
                    onChange={handleChange}
                  />
                </Grid2>
                <Grid2 size={{xs:12, sm:12, md:6, lg:6, xl:6}}>
                  <TextField
                    margin="dense"
                    size="small"
                    label="Country"
                    name="addressCountry"
                    fullWidth
                    value={patientDetails.addressCountry}
                    onChange={handleChange}
                  />
                </Grid2>
                <Grid2 size={{xs:12, sm:12, md:6, lg:6, xl:6}}>
                  <TextField
                    margin="dense"
                    size="small"
                    label="Pin Code"
                    name="addressPIN"
                    fullWidth
                    value={patientDetails.addressPIN}
                    onChange={handleChange}
                  />
                </Grid2>
              </Grid2>
            </Card>
          </Grid2>
          <Grid2 size={12}>
            <Card variant="outlined" sx={{ padding: "10px", width: "100%" }}>
              <CardHeader
                subheader="National ID"
                sx={{ paddingX: 0 }}
              ></CardHeader>
              <Grid2 container spacing={2}>
                <Grid2 size={{xs:12, sm:12, md:6, lg:6, xl:6}}>
                  <FormControl fullWidth margin="dense" size="small">
                    <InputLabel id="national-id-proof-label">
                      National ID Type
                    </InputLabel>
                    <Select
                      labelId="national-id-proof-label"
                      name="nationalIdType"
                      value={patientDetails.nationalIdType}
                      onChange={handleChange}
                      input={<OutlinedInput label="National ID Type" />}
                    >
                      {NationalIdTypes.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid2>

                <Grid2 size={{xs:12, sm:12, md:6, lg:6, xl:6}}>
                  <TextField
                    margin="dense"
                    size="small"
                    label="ID Number"
                    name="nationalId"
                    fullWidth
                    value={patientDetails.nationalId}
                    onChange={handleChange}
                  />
                </Grid2>
              </Grid2>
            </Card>
          </Grid2>
          <Grid2 size={12}>
            <Card variant="outlined" sx={{ padding: "10px", width: "100%" }}>
              <CardHeader
                subheader="Emergency Contact Details"
                sx={{ paddingX: 0 }}
              ></CardHeader>
              <Grid2 container spacing={2}>
                <Grid2 size={{xs:12, sm:12, md:4, lg:4, xl:4}}>
                  <TextField
                    margin="dense"
                    size="small"
                    variant="outlined"
                    label=" Name"
                    name="emergencyContactPerson"
                    fullWidth
                    value={patientDetails.emergencyContactPerson}
                    onChange={handleChange}
                  />
                </Grid2>

                <Grid2 size={{xs:12, sm:12, md:4, lg:4, xl:4}}>
                  <TextField
                    margin="dense"
                    size="small"
                    label="Phone"
                    name="emergencyPhone"
                    fullWidth
                    value={patientDetails.emergencyContactPhoneNumber}
                    onChange={handleChange}
                  />
                </Grid2>
                <Grid2 size={{xs:12, sm:12, md:4, lg:4, xl:4}}>
                  <TextField
                    margin="dense"
                    size="small"
                    label="Relationship"
                    name="emergencyRelationship"
                    fullWidth
                    value={patientDetails.emergencyContactRelationship}
                    onChange={handleChange}
                  />
                </Grid2>
              </Grid2>
            </Card>
          </Grid2>
        </Grid2>

        <Grid2
           size={{xs:8, sm:8, md:8, lg:2, xl:2}}
          container
          alignItems="flex-start"
          justifyContent="flex-start"
          direction="column"
        >
          <Card variant="outlined" sx={{ padding: "10px", width: "100%" }}>
            {/* Card Header */}
            <CardHeader subheader="Upload Image" sx={{ paddingX: 0 }} />

            {/* Card Content */}
            <CardContent>
              <Grid2
                container
                alignItems="center"
                justifyContent="center"
                spacing={2}
              >
                <Grid2 size={12} container justifyContent="center">
                  <img
                    src={
                      patientDetails.photo ||
                      "./placeholder-image-male-gender.webp"
                    }
                    alt="PatientPhoto"
                    width="120"
                    height="120"
                    className="border rounded p-0"
                  />
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ padding: 0 }}
                  >
                    Upload image size max 1MB
                  </Typography>
                </Grid2>

                {/* File Input (Hidden) */}
                <input
                  type="file"
                  accept="image/jpeg, image/png, image/gif, image/webp, jpg, png, gif"
                  hidden
                  id="photo-upload"
                  onChange={handlePhotoUpload}
                />

                {/* Upload and Delete Buttons */}
                <Grid2
                  container
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                >
                  {/* Upload Button */}
                  <Grid2>
                    <label htmlFor="photo-upload">
                      <IconButton component="span">
                        <PhotoCamera />
                      </IconButton>
                    </label>
                  </Grid2>

                  {/* Delete Button (Only Shown When Photo is Uploaded) */}
                  {patientDetails.photo && (
                    <Grid2>
                      <IconButton onClick={handlePhotoDelete}>
                        <Delete />
                      </IconButton>
                    </Grid2>
                  )}
                </Grid2>
              </Grid2>
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>
    </Grid2>
    </Box>
  );
}

export default NewPatient;
